import * as React from "react";
import { Context } from "../appcontext";
import { ElementHtmlAttributes, getAttributes } from "../common/common";
import {
  CSNclTabControlMetadata,
  GlyphId,
  IconPosition,
  Orientation,
  UFUpdateControl,
  UpdateControl,
  UpdateDockControl,
  UpdateTabControl,
  UpdatePageControl,
  K2Boolean,
} from "../common/communication.base";
import {
  NclBaseTabControl,
  NclContainerBase,
  NclControlBase,
  NclDockControl,
  NclMultiContent,
  NclPage,
  NclVRTabControl,
  UFOverAlign,
  UFOverAlignListener,
} from "../common/components.ncl";
import { ViewRealizer, ViewRealizerManager } from "../viewrealizer";
import { GenerateControl } from "./K2GenerateControl";
import { AcquireControl, K2ComponentState, StyleHelper, withContext, WithContextPlacementProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2RuleWithVCXProps, K2StyleRenderer, matchSmallMediaBreakpoint } from "./k2StyleRenderer";
import { K2TruncateText } from "./K2TruncateText";
import { ViewRealizerReact } from "./ViewRealizerReact";
import { VCXColorMap } from "../common/visualContext";
import { K2RibbonAction } from "./K2RibbonAction";
import { K2Action } from "./K2Action";
import { K2Scroll } from "./K2Scroll";
import { reduce } from "highcharts";
import { SplitterContext } from "./K2SplitterPanel";
import { IRenderer } from "fela";
import { UpdateContext } from "../context";

type TBIconProps = K2RuleWithVCXProps & {
  iconPosition?: IconPosition;
};

type ResponsiveTabProps = K2RuleWithVCXProps & {
  isShow: boolean;
  depth?: number;
};

type ResponsiveButtonProps = K2RuleWithVCXProps & {
  accent: boolean;
};

type ResponsiveTabsProps = ResponsiveTabProps & {
  first: boolean;
  hide: boolean;
  listDetail: K2Boolean;
  dynamic: boolean;
  menuOpen: boolean;
  index: number;
};

const tabButtonRules = {
  horizontalResponsive: (props: ResponsiveTabsProps) => ({
    display: "flex",
    flex: "0 0 auto",
    small: {
      height: "100%",
      display: props.isShow ? "flex" : "none",
      zIndex: props.depth ? props.depth + 1 : "unset",
    },
  }),
  horizontalResponsiveWithAnimation: (props: ResponsiveTabsProps, renderer: IRenderer) => ({
    display: "flex",
    small: {
      height: "100%",
      display: props.hide ? "none" : "flex",
      zIndex: props.depth ? props.depth : !props.hide ? 2 : "unset",
      animationName: props.listDetail ? (props.menuOpen ? "list-detail-open" : "list-detail-close") : props.menuOpen ? "main-menu-open" : "main-menu-close",
      animationDuration: "0.25s",
      flex: props.listDetail ? "1 0 auto" : "0 0 auto",
      order: props.index,
    },
  }),
  responsiveMenuIcon: (props: ResponsiveTabProps) => ({
    display: "flex",
    small: {
      transform: props.isShow ? "rotate(180deg)" : "none",
    },
  }),
  responsiveButton: (props: ResponsiveButtonProps) => ({
    display: "none",
    small: {
      alignItems: "center",
      flex: "none",
      display: "flex",
      border: 0,
      backgroundColor: "inherit",
      height: "100%",
      width: "auto",
      paddingLeft: props.vcx.sizeMap(4) + "px",
    },
  }),
  stretch: (props: K2RuleWithVCXProps) => ({
    display: "none",
    small: {
      display: "flex",
      flex: "1 1 auto",
    },
  }),
  togglePreview: (props: K2RuleWithVCXProps) => ({
    display: "none",
    small: {
      display: "flex",
      borderLeft: `1px solid ${props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1)}`,
      width: `${props.vcx.sizeMap(30)}px`,
      justifyContent: "center",
      alignItems: "center",
    },
  }),
  base: (props: K2RuleWithVCXProps & { listDetail: K2Boolean; dynamic: K2Boolean }) => ({
    borderTop: "0",
    borderLeft: "0",
    backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
    textAlign: "center" as "center",
    textDecoration: "none",
    color: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
    justifyContent: "center",
    alignItems: "center",
    padding: props.vcx.sizeMap(3) + "px",
    flexDirection: "row" as "row",
    small: {
      backgroundColor: props.listDetail
        ? "#fff !important"
        : props.dynamic
        ? props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck) + " !important"
        : null,
      color: props.listDetail ? props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1) : null,
    },
  }),
  selected: (props: K2RuleWithVCXProps & { listDetail: K2Boolean }) => ({
    backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck) + " !important",
    color: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
    small: {
      display: "flex",
      backgroundColor: props.listDetail ? "#fff !important" : null,
      color: props.listDetail ? props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1) : null,
    },
  }),
  vertical: (props: K2RuleWithVCXProps) => ({
    height: 2.5 * props.vcx.ExpanderControl.GetHFHeight() - props.vcx.sizeMap(4) + "px", //width panel
    width: "100%",
    borderRight: "0",
    borderBottom: props.vcx.sizeMap(1) + "px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
    "> div": {
      width: "100%",
    },
    small: {
      height: "100%",
      flex: "1",
      borderBottom: 0,
    },
  }),
  horizontal: (props: K2RuleWithVCXProps) => ({
    minWidth: 1.3 * props.vcx.ExpanderControl.GetHFHeight() - props.vcx.sizeMap(4) + "px",
    borderBottom: "0",
    flex: "none",
    borderRightWidth: props.vcx.sizeMap(1) + "px",
    borderRightStyle: "solid" as "solid",
    borderRightColor: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
    display: "flex",
    justifyContent: "center",
    small: {
      width: "100%",
      flex: "1",
      borderRight: 0,
    },
  }),
  btnContent: (props: TBIconProps & { listDetail: K2Boolean }) => ({
    gap: "5px",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: props.iconPosition % 2 === 0 ? ("row" as "row") : ("column" as "column"),
    flex: 1,
    small: {
      flexDirection: "row" as "row",
    },
    "> span": {
      zIndex: 1,
    },
    "& svg": {
      fill: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
      small: {
        fill: props.listDetail ? props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1) : props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
      },
    },
  }),
  content: (props: K2RuleWithVCXProps) => ({
    flexDirection: "row" as "row",
    flex: "1 0 0%",
    justifyContent: "center",
    small: {
      flex: "0 1 auto",
      marginLeft: "10px",
    },
    "> p": {
      width: "100%",
      textAlign: "center",
      alignSelf: "center",
      textOverflow: "inherit",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      flexWrap: "wrap",
      whiteSpace: "pre-wrap",
      justifyContent: "center",
      small: {
        width: "auto",
      },
    },
  }),
};

interface TabButtonProps extends WithContextPlacementProps {
  orientation: Orientation;
  currentPage: string;
  iconPosition: IconPosition;
  showExpandButton?: boolean;
  forceShow?: boolean;
  isShowResponsiveMenu: boolean;
  showResponsiveMenu?: () => void;
  getCurrentPage?: (ref: HTMLDivElement) => void;
  listDetail?: K2Boolean;
  lastPage?: string;
  onMenu?: boolean;
  index?: number;
}

class TabPage extends React.PureComponent<TabButtonProps, K2ComponentState<UpdatePageControl> & { shouldRenderMenuButtons: boolean }> {
  static displayName = `K2TabButton`;
  private ref: HTMLDivElement;
  private control: NclPage;
  static contextType = SplitterContext;

  constructor(props: TabButtonProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclPage;
    }) as NclPage;
    this.state = { data: this.control.init(this) as UpdatePageControl, vcxVersion: -1, shouldRenderMenuButtons: false };
  }

  private get GlyphId(): GlyphId {
    if (this.state.data.GlyphId) return this.state.data.GlyphId;
    return this.control.MetaData.FrgtData.Icon;
  }

  public updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdatePageControl>) => {
      return { data: state as UpdatePageControl };
    });
  }

  public updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  scrollInToView() {
    if (this.ref) {
      if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0]) return;
      this.ref.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  }

  private getClassName(): string {
    let className: string = this.props.renderer.renderRule(tabButtonRules.base, {
      vcx: this.control.VCX,
      listDetail: this.props.listDetail,
      dynamic: this.control.Ncl.IsDynamic,
    });

    if (this.props.orientation === Orientation.foVertical) {
      className += " " + this.props.renderer.renderRule(tabButtonRules.vertical, { vcx: this.control.VCX });
    } else {
      className += " " + this.props.renderer.renderRule(tabButtonRules.horizontal, { vcx: this.control.VCX });
    }

    if (this.props.currentPage === this.control.Ncl.PageUID) {
      className +=
        " " +
        this.props.renderer.renderRule(tabButtonRules.selected, {
          vcx: this.control.VCX,
          listDetail: this.props.listDetail,
        });
    }
    return className;
  }

  componentDidUpdate() {
    if (this.props.currentPage === this.control.Ncl.PageUID) {
      // this.props.getCurrentPage(this.ref);
    }

    if (this.props.isShowResponsiveMenu) {
      this.setState({ shouldRenderMenuButtons: true });
    }
  }

  handleAnimationEnd = () => {
    if (this.props.isShowResponsiveMenu == false) {
      this.setState({ shouldRenderMenuButtons: false });
    }
  };

  private handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    this.control.showContextMenu();
    e.preventDefault();
  };

  render() {
    let hideMenuButtons = false;
    let mainPanel = this.control instanceof NclVRTabControl;

    if (
      (this.props.listDetail || this.control.Ncl.IsDynamic) &&
      this.state.shouldRenderMenuButtons === false &&
      this.props.currentPage !== this.control.Ncl.PageUID &&
      !(this.control.Ncl.IsDynamic && this.props.onMenu && this.props.lastPage === this.control.Ncl.PageUID) // na strance s jednotlivymi moduly zajisti vykresleni posledni zobrazene stranky
    ) {
      hideMenuButtons = true;
    }

    if (this.state.data.Hide === true || !this.control.Content) return null;
    let glId = this.GlyphId;
    let addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);
    let classname: string = "";
    if (this.props.className) {
      classname = this.props.className + " ";
    }
    let useDepth: boolean = false;
    if (this.props.forceShow) {
      if (this.control.getTabParent() instanceof NclVRTabControl) {
        useDepth = this.control.Ncl.IsDynamic === 1;
      } else {
        useDepth = true;
      }
    }

    return (
      <div
        style={StyleHelper(this.control, Object.assign({}, { width: "auto" }, this.props.style))}
        className={
          classname +
          this.props.renderer.renderRule(
            this.props.listDetail || this.control.Parent instanceof NclVRTabControl // && this.props.index > 0
              ? tabButtonRules.horizontalResponsiveWithAnimation
              : tabButtonRules.horizontalResponsive,
            {
              vcx: this.control.VCX,
              first: this.props.showExpandButton,
              depth: useDepth ? this.control.Depth + 1 : 0,
              isShow: this.props.forceShow == true || this.props.currentPage === this.control.Ncl.PageUID,
              hide: hideMenuButtons,
              listDetail: this.props.listDetail,
              dynamic: mainPanel,
              menuOpen: this.props.isShowResponsiveMenu,
              index: this.props.index,
            }
          )
        }
        onAnimationEnd={this.handleAnimationEnd}
        onContextMenu={this.control.Parent instanceof NclVRTabControl ? this.handleContextMenu : undefined}
      >
        <div style={{ flex: "1 1 auto" }}>
          <button
            data-k2-test-id={"page_" + this.control.Content.MetaData.Name}
            className={this.getClassName()}
            onClick={this.handleClick}
            {...addAttributes}
            onContextMenu={this.control.Parent instanceof NclVRTabControl ? this.handleContextMenu : undefined}
          >
            <div
              ref={(ref) => {
                this.ref = ref;
              }}
              className={this.props.renderer.renderRule(tabButtonRules.btnContent, {
                vcx: this.control.VCX,
                iconPosition: this.GlyphId ? this.props.iconPosition : null,
                listDetail: this.props.listDetail,
              })}
            >
              {!glId ? null : <K2Img glyphId={glId} vcx={this.control.VCX} height={this.control.VCX.sizeMap(20)} width={this.control.VCX.sizeMap(20)} />}

              {(glId && this.props.iconPosition === IconPosition.ipCenter) || !this.state.data.Title ? null : (
                <>
                  <div className={this.props.renderer.renderRule(tabButtonRules.content, { vcx: this.control.VCX })}>
                    <K2TruncateText className={this.props.renderer.renderFontRule(this.control.VCX.BookTabControl.AccessorFont, this.control.VCX)}>
                      {this.state.data.Title}
                    </K2TruncateText>
                    {this.props.showExpandButton == true && (
                      <div
                        className={this.props.renderer.renderRule(tabButtonRules.responsiveButton, {
                          vcx: this.control.VCX,
                          accent: this.props.forceShow == false || this.control.Ncl.PageUID === this.props.currentPage,
                        })}
                      >
                        <K2Img
                          glyphId={"wui*roofdown"}
                          width={this.control.VCX.sizeMap(12)}
                          height={this.control.VCX.sizeMap(12)}
                          vcx={this.control.VCX}
                          strokeColor={this.control.VCX.getColor(
                            this.props.listDetail ? this.control.VCX.Data.ColorMap.BaseColorBck1 : this.control.VCX.Data.ColorMap.BaseColorFrg1
                          )}
                          className={this.props.renderer.renderRule(tabButtonRules.responsiveMenuIcon, {
                            vcx: this.control.VCX,
                            isShow: this.props.isShowResponsiveMenu,
                            depth: 0,
                          })}
                        />
                      </div>
                    )}
                  </div>
                  <div className={this.props.renderer.renderRule(tabButtonRules.stretch, { vcx: this.control.VCX })}></div>
                </>
              )}

              {this.control.Ncl.IsDynamic === 1 && this.state.data.IsPinned === false && (
                <span onClick={this.handleCloseClick} data-k2-test-id={"close_" + this.control.Content.MetaData.Name}>
                  <K2Img
                    glyphId={"wui*close"}
                    vcx={this.control.VCX}
                    width={this.control.VCX.sizeMap(16)}
                    height={this.control.VCX.sizeMap(16)}
                    strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorFrg1)}
                  />
                </span>
              )}
              {this.control.Ncl.IsDynamic === 1 && this.state.data.IsPinned === true && (
                <K2Img
                  glyphId={"wui*pinleftdown"}
                  vcx={this.control.VCX}
                  width={this.control.VCX.sizeMap(16)}
                  height={this.control.VCX.sizeMap(16)}
                  strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorFrg1)}
                />
              )}
            </div>
          </button>
        </div>
        {this.props.listDetail === 1 && this.props.currentPage === this.control.Ncl.PageUID && !this.props.isShowResponsiveMenu && (
          <div className={this.props.renderer.renderRule(tabButtonRules.togglePreview, { vcx: this.control.VCX })} onClick={() => this.context?.setRatio()}>
            <K2Img
              glyphId={this.context?.ratio === 1 ? "wui*left" : "wui*right"}
              vcx={this.control.VCX}
              width={20}
              height={20}
              strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1)}
            />
          </div>
        )}
      </div>
    );
  }

  private handleClick = () => {
    this.control.getTabParent().changeCurrentPage(this.control.Ncl.PageUID);

    if (this.props.showExpandButton === true && this.props.showResponsiveMenu && matchSmallMediaBreakpoint()) this.props.showResponsiveMenu.call(this);
  };

  private handleCloseClick = (e: any) => {
    (this.control.getTabParent() as NclVRTabControl).closePage(this.control.Ncl.PageUID);
    e.stopPropagation();
  };
}

const K2TabPage = withContext(TabPage);

type TabProps = K2RuleWithVCXProps & {
  orientation: Orientation;
  main?: boolean;
  pages?: number;
  companyColor?: string;
  listDetail?: K2Boolean;
  dynamic?: boolean;
  onMenu?: boolean;
  btns?: number;
};

type TabContentProps = K2RuleWithVCXProps & {
  showResponsiveMenu: boolean;
};

const tabControlRules = {
  base: (props: TabProps) => ({
    flexDirection: props.orientation == Orientation.foVertical ? ("row" as "row") : ("column" as "column"),
    small: {
      flexDirection: "column" as "column",
      padding: "0 !important",
    },
  }),
  basePanel: (props: TabProps) => ({
    flexDirection: props.orientation == Orientation.foVertical ? ("column" as "column") : ("row" as "row"),
    borderBottom:
      props.orientation == Orientation.foHorizontal
        ? props.vcx.sizeMap(4) + "px solid " + (props.companyColor ? props.companyColor : props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck))
        : "none",
    height:
      props.orientation == null || props.orientation == Orientation.foHorizontal
        ? props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(6) + "px"
        : "100%",
    flex: "0 0 auto",
    marginLeft: props.main ? "3px" : null,
    small: {
      flexDirection: "row" as "row",
      borderBottom:
        props.main === true
          ? props.vcx.sizeMap(4) + "px solid " + (props.companyColor ? props.companyColor : props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck))
          : "none",
      height: `${props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(6)}px`,
      position: props.listDetail ? "absolute" : null,
      width: "100%",
      bottom: props.listDetail ? 0 : null,
      marginLeft: 0,
    },
  }),
  tabPanel: (props: TabProps) => ({
    flexDirection: props.orientation == Orientation.foHorizontal ? ("row" as "row") : ("column" as "column"),
    flex: props.orientation == Orientation.foHorizontal ? "0 1 auto" : "1 0 auto",
    width: props.orientation == Orientation.foVertical ? 2.5 * props.vcx.ExpanderControl.GetHFHeight() + "px" : "100%",
    borderRight:
      props.orientation == Orientation.foVertical
        ? props.vcx.sizeMap(4) + "px solid " + (props.companyColor ? props.companyColor : props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck))
        : "none",
    backgroundColor: props.orientation == Orientation.foVertical ? props.vcx.ColorMap.getColor(props.vcx.Data.ColorMap.BaseColorBck1) : "unset",
    small: {
      borderRight: 0,
      flex: 1,
      height: "100%",
      flexDirection: props.listDetail ? "column-reverse" : "column",
      backgroundColor: props.listDetail
        ? props.vcx.ColorMap.getColor(props.vcx.Data.ColorMap.BaseColorFrg1)
        : props.dynamic
        ? props.vcx.ColorMap.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck)
        : props.vcx.ColorMap.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
      boxShadow: props.listDetail ? "0px 0px 10px 0px rgb(0 0 0 / 50%)" : null,
      width: "100%",
    },
  }),
  tabContent: (props: K2RuleWithVCXProps & { mainTC: boolean; VRTC: boolean; listDetailTC: K2Boolean; orientation: Orientation }) => ({
    flex: "1 1 auto",
    padding: !props.mainTC && !props.VRTC ? `${props.vcx.sizeMap(props.vcx.Data.MarginY) * 2}px ${props.vcx.sizeMap(props.vcx.Data.MarginX) * 2}px` : null,
    border: !props.mainTC && !props.VRTC ? `1px solid ${props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1)}` : null,
    borderLeft:
      props.orientation === Orientation.foVertical || props.VRTC ? "none !important" : `1px solid ${props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1)}`,
    borderTop:
      props.mainTC || props.orientation === Orientation.foHorizontal
        ? "none !important"
        : `1px solid ${props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1)}`,
    small: {
      flex: props.VRTC || props.listDetailTC ? "0 1 auto" : "1 1 auto",
      height: props.VRTC || props.listDetailTC ? `calc(100% - ${props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(6)}px)` : null,
      padding: `${props.vcx.sizeMap(props.vcx.Data.MarginY)}px ${props.vcx.sizeMap(props.vcx.Data.MarginX)}px`,
      border: "none",
    },
  }),
  tabContentOverlay: (props: TabContentProps) => ({
    display: "none",
    small: {
      left: 0,
      top: 0,
      position: "absolute",
      width: "100%",
      height: "100%",
      backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
      opacity: "0.5",
      display: props.showResponsiveMenu ? "flex" : "none",
      zIndex: 1,
    },
  }),
  staticPage: (props: K2RuleWithVCXProps) => ({
    borderRight: props.vcx.sizeMap(1) + "px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
  }),

  tabStaticPanel: (props: K2RuleWithVCXProps) => ({
    display: "none",
    small: {
      display: "flex",
    },
  }),
  tabActionPanel: (props: TabProps) => ({
    backgroundColor: props.companyColor ? props.companyColor : props.vcx.ColorMap.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
    borderRight:
      props.orientation == Orientation.foVertical
        ? props.vcx.sizeMap(4) + "px solid " + (props.companyColor ? props.companyColor : props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck))
        : "none",
    flexDirection: props.orientation == Orientation.foVertical ? ("column" as "column") : ("row" as "row"),
    width:
      props.orientation == Orientation.foHorizontal
        ? props.btns > 0
          ? props.btns * (props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(4))
          : props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(4) + "px"
        : "100%",
    height:
      props.orientation == Orientation.foVertical
        ? props.btns > 0
          ? props.btns * 2.5 * props.vcx.ExpanderControl.GetHFHeight()
          : 2.5 * props.vcx.ExpanderControl.GetHFHeight() + "px"
        : "100%",
    "> div": {
      borderTop: "1px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
    },
    small: {
      flexDirection: "row" as "row",
      height: "100%",
      width:
        props.btns > 0
          ? props.btns * (props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(4))
          : props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(4) + "px",
      borderLeft: "none",
      borderRight: "none",
      "> div": {
        borderLeft: "1px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorFrg1),
        borderTop: "none",
      },
    },
  }),
};

interface TabControlState extends K2ComponentState<UpdateTabControl> {
  isShowResponsiveMenu?: boolean;
  LastCurrentPage?: string;
  tab?: HTMLDivElement;
}

class _TabControl extends React.Component<WithContextPlacementProps, TabControlState> {
  static displayName = `K2TabControl`;
  private pageRefs: Array<TabPage>;
  private control: NclBaseTabControl<CSNclTabControlMetadata, UpdateTabControl>;
  private content: HTMLElement;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclBaseTabControl;
    }) as NclBaseTabControl<CSNclTabControlMetadata, UpdateTabControl>;
    this.state = { data: this.control.init(this) as UpdateTabControl, vcxVersion: -1 };
    this.pageRefs = new Array<TabPage>();
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: TabControlState) => {
      if (prevState.data.CurrentPage != (state as UpdateTabControl).CurrentPage) {
        return { data: state as UpdateTabControl, isShowResponsiveMenu: false, LastCurrentPage: prevState.data.CurrentPage };
      }
      return { data: state as UpdateTabControl };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  getCurrentPage = (currentPage: HTMLDivElement) => {
    this.setState({ tab: currentPage });
  };

  render() {
    this.pageRefs = [];
    let mainPanel: boolean = this.control instanceof NclVRTabControl;
    let countPages: number = mainPanel
      ? this.control.Pages.filter((value) => {
          return value.Ncl.IsDynamic === 1;
        }).count()
      : this.control.Pages.count();
    let tabIndex: number = 0;
    let currentIsStatic: boolean;

    return (
      <div
        className={this.props.renderer.renderRule(tabControlRules.base, { vcx: this.control.VCX, orientation: this.getOrientation() })}
        style={StyleHelper(this.control, { ...this.props.style, minHeight: this.control.ComputedMinHeightWithMargin + "px" })}
      >
        <div
          className={this.props.renderer.renderRule(tabControlRules.basePanel, {
            companyColor: this.control.CompanyColor,
            vcx: this.control.VCX,
            orientation: this.getOrientation(),
            main: mainPanel,
            listDetail: !this.control.Btns && this.control.Ncl.ListDetailTabControl,
          })}
        >
          <K2Scroll tab={this.state.tab} orientation={this.getOrientation()} renderer={this.props.renderer} control={this.control}>
            {this.control.Pages.map((value: NclPage, key: number) => {
              if (mainPanel && !value.Ncl.IsDynamic) {
                if (!currentIsStatic && this.state.data.CurrentPage === value.Ncl.PageUID) {
                  currentIsStatic = true;
                }
                return (
                  <K2TabPage
                    getCurrentPage={this.getCurrentPage}
                    className={this.props.renderer.renderRule(tabControlRules.staticPage, { vcx: this.control.VCX })}
                    isShowResponsiveMenu={false}
                    showExpandButton={false}
                    forceShow={true}
                    key={value.MetaData.ControlUID}
                    controlUID={value.MetaData.ControlUID}
                    vrUID={this.control.getRealizerUID()}
                    orientation={this.getOrientation()}
                    currentPage={this.state.data.CurrentPage}
                    iconPosition={this.control.Ncl.FrgtData.TabIconPosition}
                  />
                );
              }
            })}
            <div
              className={this.props.renderer.renderRule(tabControlRules.tabPanel, {
                companyColor: this.control.CompanyColor,
                vcx: this.control.VCX,
                orientation: this.getOrientation(),
                pages: countPages,
                listDetail: !this.control.Btns && this.control.Ncl.ListDetailTabControl,
                onMenu: this.state.data.CurrentPage === this.control.Pages.get(0).Ncl.PageUID,
              })}
            >
              {this.control.Pages.map((value: NclPage, key: number) => {
                if (!mainPanel || value.Ncl.IsDynamic) {
                  tabIndex++;
                  return (
                    <K2TabPage
                      showExpandButton={
                        ((this.state.isShowResponsiveMenu && tabIndex === 1) ||
                          ((this.state.data.CurrentPage === value.Ncl.PageUID || this.state.LastCurrentPage == value.Ncl.PageUID) &&
                            !this.state.isShowResponsiveMenu)) &&
                        countPages > 1
                      }
                      forceShow={this.state.isShowResponsiveMenu || (this.state.LastCurrentPage == value.Ncl.PageUID && currentIsStatic)}
                      isShowResponsiveMenu={this.state.isShowResponsiveMenu}
                      showResponsiveMenu={this.showResponsiveMenu}
                      key={value.MetaData.ControlUID}
                      controlUID={value.MetaData.ControlUID}
                      vrUID={this.control.getRealizerUID()}
                      orientation={this.getOrientation()}
                      currentPage={this.state.data.CurrentPage}
                      iconPosition={this.control.Ncl.FrgtData.TabIconPosition}
                      ref={(ref) => {
                        if (ref && ref instanceof TabPage) this.pageRefs.push(ref);
                      }}
                      getCurrentPage={this.getCurrentPage}
                      listDetail={!this.control.Btns && this.control.Ncl.ListDetailTabControl}
                      lastPage={this.state.LastCurrentPage}
                      onMenu={this.state.data.CurrentPage === this.control.Pages.get(0).Ncl.PageUID}
                      index={!this.control.Btns && this.control.Ncl.ListDetailTabControl ? this.control.Pages.size - key : key}
                    />
                  );
                }
              })}
            </div>
          </K2Scroll>
          {this.control.Btns && this.control.Btns.length > 0 && (
            <div
              className={this.props.renderer.renderRule(tabControlRules.tabActionPanel, {
                companyColor: this.control.CompanyColor,
                vcx: this.control.VCX,
                orientation: this.getOrientation(),
                btns: this.control.Btns.length,
              })}
            >
              {this.control.Btns.map((item, index) => {
                return (
                  <K2RibbonAction
                    heightIcon={20}
                    key={`tab_btn_${index}`}
                    style={{ width: "100%", height: "100%" }}
                    controlUID={item.MetaData.ControlUID}
                    vrUID={this.control.getRealizerUID()}
                    color={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorFrg1)}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div
          ref={(ref) => {
            this.content = ref;
          }}
          className={this.props.renderer.renderRule(tabControlRules.tabContent, {
            vcx: this.control.VCX,
            mainTC: this.control.Btns != null,
            VRTC: this.control instanceof NclVRTabControl,
            listDetailTC: this.control.Ncl.ListDetailTabControl,
            orientation: this.getOrientation(),
          })}
        >
          <div
            onClick={this.handleOutsideClick}
            className={this.props.renderer.renderRule(tabControlRules.tabContentOverlay, {
              vcx: this.control.VCX,
              showResponsiveMenu: this.state.isShowResponsiveMenu,
            })}
          />
          {this.state.data.CurrentPage &&
            this.control.Pages.map((control) => {
              if (
                this.control instanceof NclVRTabControl ||
                control.Content instanceof NclVRTabControl ||
                control.Content.MetaData.ControlUID == this.state.data.CurrentPage
              ) {
                return GenerateControl(control.Content);
              }
            })}
        </div>
      </div>
    );
  }

  componentDidUpdate() {
    if (this.state.isShowResponsiveMenu && this.pageRefs.length > 0) {
      this.pageRefs[this.pageRefs.length - 1].scrollInToView();
    }
  }

  private handleOutsideClick = () => {
    if (this.state.isShowResponsiveMenu == true) {
      this.setState({ isShowResponsiveMenu: false });
    }
  };

  private showResponsiveMenu = () => {
    this.setState({ isShowResponsiveMenu: !this.state.isShowResponsiveMenu });
  };

  private getOrientation(): Orientation {
    return this.control.Ncl.ListDetailTabControl ? Orientation.foVertical : Orientation.foHorizontal;
  }
}

export const K2TabControl = withContext(_TabControl);

class _PageHolder extends React.Component<WithContextPlacementProps, K2ComponentState<UpdateDockControl>> {
  static displayName = `K2TabPlaceHolder`;
  private control: NclDockControl;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(
      this.props.controlUID,
      this.props.vrUID,
      (ctrl) => {
        return ctrl instanceof NclDockControl;
      },
      false
    ) as NclDockControl;

    if (!this.control) {
      this.control = Context.getApplication().getMainDockControl();
    }

    if (!this.control) {
      throw new Error("Dont't exist dock control.");
    }

    this.state = { data: this.control.init(this) as UpdateDockControl, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateDockControl>) => {
      return { data: state as UpdateDockControl };
    });
  }

  dock(state: UpdateDockControl): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState({ data: state }, resolve);
    });
  }

  undock(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState({ data: { DockRealizerUID: null } as UpdateDockControl }, resolve);
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    let vr: ViewRealizer = ViewRealizerManager.getViewRealizer(this.state.data.DockRealizerUID);
    if (vr == null) {
      return null;
    } else {
      return <ViewRealizerReact VRUID={vr.getRealizerUID()} overlayBck={false} />;
    }
  }
}

export const K2PageHolder = withContext(_PageHolder);

class _MultiContent extends React.PureComponent<WithContextPlacementProps, K2ComponentState<UpdateTabControl>> {
  static displayName = `K2MultiContent`;
  private control: NclMultiContent;
  static contextType = UpdateContext;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclMultiContent;
    }) as NclMultiContent;
    this.state = { data: this.control.init(this) as UpdateTabControl, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateTabControl>) => {
      return { data: state as UpdateTabControl };
    });

    this.context.update();
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    return (
      <div style={StyleHelper(this.control, this.props.style)}>
        {this.control.Pages.map((control) => {
          if (control.Content.MetaData.ControlUID == this.state.data.CurrentPage) {
            return GenerateControl(control.Content);
          }
        })}
      </div>
    );
  }
}

export const K2MultiContent = withContext(_MultiContent);
